import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Calendar,
  BookOpen,
  User,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  TrendingUp,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const BatchesPage = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewingBatch, setViewingBatch] = useState(null);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/batch/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success && data.data) {
        // Map API response to match UI
        const mappedBatches = data.data.map((batch) => ({
          ...batch,
          currentStudents: batch.currentStudents || 0, // Handle missing field
        }));
        setBatches(mappedBatches);
      }
    } catch (error) {
      toast.error("Failed to fetch batches");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (batchId) => {
    if (!window.confirm("Are you sure you want to delete this batch?")) {
      return;
    }

    try {
      await axios.delete(`${server}/batch/deleteById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          batchId: batchId,
        },
      });
      toast.success("Batch deleted successfully");
      fetchBatches();
    } catch (error) {
      toast.error("Failed to delete batch");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      upcoming: "bg-blue-500/20 text-blue-400 border-blue-500",
      ongoing: "bg-green-500/20 text-green-400 border-green-500",
      completed: "bg-gray-500/20 text-gray-400 border-gray-500",
      cancelled: "bg-red-500/20 text-red-400 border-red-500",
    };
    return colors[status] || colors.upcoming;
  };

  const filteredBatches = batches.filter((batch) => {
    const matchesSearch =
      `${batch.batchName} ${batch.batchCode} ${batch.courseName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    const matchesStatus =
      filterStatus === "all" || batch.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <motion.div
        className="max-w-7xl mx-auto space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl">
                <Users className="w-8 h-8 text-white" />
              </div>
              Batches Management
            </h1>
            <p className="text-gray-400 text-lg">
              Manage all batches and student groups
            </p>
          </div>

          <button
            onClick={() => navigate("/my/createBatch")}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-br from-blue-600 to-cyan-600 text-white rounded-lg hover:shadow-lg hover:shadow-blue-500/20 transition-all font-semibold"
          >
            <Plus className="w-5 h-5" />
            Create Batch
          </button>
        </div>

        {/* Filters */}
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search batches..."
                className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
            >
              <option value="all">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Batches"
            value={batches.length}
            icon={Users}
            color="text-blue-400"
            bg="bg-blue-500/20"
          />
          <StatCard
            title="Ongoing"
            value={batches.filter((b) => b.status === "ongoing").length}
            icon={CheckCircle}
            color="text-green-400"
            bg="bg-green-500/20"
          />
          <StatCard
            title="Upcoming"
            value={batches.filter((b) => b.status === "upcoming").length}
            icon={Clock}
            color="text-orange-400"
            bg="bg-orange-500/20"
          />
          <StatCard
            title="Total Students"
            value={batches.reduce((sum, b) => sum + b.currentStudents, 0)}
            icon={TrendingUp}
            color="text-purple-400"
            bg="bg-purple-500/20"
          />
        </div>

        {/* Batches Table */}
        <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : filteredBatches.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No batches found</p>
              <button
                onClick={() => navigate("/my/createBatch")}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create First Batch
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900 border-b border-gray-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Batch Details
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Course
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Duration
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Students
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Teacher
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredBatches.map((batch) => (
                    <tr
                      key={batch.id}
                      className="hover:bg-gray-900/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-white font-medium">
                            {batch.batchName}
                          </div>
                          <div className="text-gray-400 text-sm">
                            {batch.batchCode}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        {batch.courseName}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="text-gray-300">
                            {new Date(batch.startDate).toLocaleDateString()}
                          </div>
                          <div className="text-gray-500">
                            to {new Date(batch.endDate).toLocaleDateString()}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-full bg-gray-700 rounded-full h-2 max-w-[100px]">
                            <div
                              className="bg-blue-500 h-2 rounded-full"
                              style={{
                                width: `${
                                  (batch.currentStudents / batch.maxStudents) *
                                  100
                                }%`,
                              }}
                            ></div>
                          </div>
                          <span className="text-white text-sm">
                            {batch.currentStudents}/{batch.maxStudents}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        {batch.teacherName}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            batch.status
                          )}`}
                        >
                          {batch.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                            title="View"
                            onClick={() => setViewingBatch(batch)}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            className="p-2 text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                            title="Edit"
                            onClick={() =>
                              navigate(`/my/editBatch/${batch.id}`, {
                                state: { batch },
                              })
                            }
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(batch.id)}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {viewingBatch && (
          <BatchDetailsModal
            batch={viewingBatch}
            getStatusColor={getStatusColor}
            onClose={() => setViewingBatch(null)}
          />
        )}
      </motion.div>
    </div>
  );
};

const BatchDetailsModal = ({ batch, onClose, getStatusColor }) => {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-800 rounded-2xl p-6 max-w-2xl w-full border border-gray-700 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✖
        </button>

        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
          <Users className="w-6 h-6 text-blue-400" />
          Batch Details
        </h2>

        <div className="space-y-6">
          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="text-xl font-bold text-white">
                  {batch.batchName}
                </h3>
                <p className="text-blue-400 font-medium">{batch.batchCode}</p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                  batch.status
                )}`}
              >
                {batch.status}
              </span>
            </div>
            <p className="text-gray-400 mt-2">
              {batch.description || "No description available."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="w-4 h-4 text-purple-400" />
                <span className="text-gray-400 text-sm">Course</span>
              </div>
              <p className="text-white font-medium">{batch.courseName}</p>
            </div>
            <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <User className="w-4 h-4 text-green-400" />
                <span className="text-gray-400 text-sm">Teacher</span>
              </div>
              <p className="text-white font-medium">
                {batch.teacherName || "Not Assigned"}
              </p>
            </div>
            <div className="bg-gray-900 p-4 rounded-xl border border-gray-700 col-span-2">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-orange-400" />
                <span className="text-gray-400 text-sm">Schedule</span>
              </div>
              <div className="flex gap-8 text-white font-medium">
                <div>
                  <span className="text-gray-500 text-xs block">
                    Start Date
                  </span>
                  {new Date(batch.startDate).toLocaleDateString()}
                </div>
                <div>
                  <span className="text-gray-500 text-xs block">End Date</span>
                  {new Date(batch.endDate).toLocaleDateString()}
                </div>
              </div>
            </div>

            <div className="bg-gray-900 p-4 rounded-xl border border-gray-700 col-span-2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span className="text-gray-400 text-sm">
                    Students Enrolled
                  </span>
                </div>
                <span className="text-white font-medium">
                  {batch.currentStudents} / {batch.maxStudents}
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      (batch.currentStudents / batch.maxStudents) * 100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, bg }) => {
  return (
    <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 ${bg} rounded-lg`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <span className="text-gray-400 text-sm">{title}</span>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  );
};

export default BatchesPage;
